import { useEffect, useMemo, useState } from 'react'
import { Shield, Plus, Trash2, X as XIcon, Lock, Check } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { pedirExclusao } from '@/lib/confirmarExclusao'
import { useAuth, cn } from '@/lib/auth'
import { getNav } from '@/components/layout/Sidebar'

interface Perfil {
  id: number
  nome: string
  slug: string
  base: string
  permissoes: Record<string, boolean> | null
  is_sistema: boolean
  ativo: boolean
}

const BASES = [
  { value: 'admin', label: 'Administrador', hint: 'acesso total (ignora bloqueios de tela)' },
  { value: 'gerente', label: 'Gerente', hint: 'gestão (vendas, estoque, relatórios)' },
  { value: 'financeiro', label: 'Financeiro', hint: 'financeiro + relatórios' },
  { value: 'operador', label: 'Operador', hint: 'operação (PDV, vendas)' },
]

const BASE_COLORS: Record<string, string> = {
  admin: 'bg-brand-100 text-brand-800',
  gerente: 'bg-indigo-100 text-indigo-800',
  financeiro: 'bg-emerald-100 text-emerald-800',
  operador: 'bg-gray-100 text-gray-700',
}

interface CatGroup { label: string; items: { to: string; label: string }[] }

// Rotas OPT-IN: escondidas por padrão (só aparecem com permissão = true). Tem que
// bater com o OPT_IN do Sidebar (filtrarPorPermissoes). Ex.: PDV-online.
const OPT_IN = new Set<string>(['/pdv-online'])

// Catálogo de telas = espelho do menu (getNav). Agrupa por seção; itens avulsos
// (Dashboard, PDV, módulos sem submenu) vão pro grupo "Telas principais".
function useCatalogo(empresaId: number | null): CatGroup[] {
  return useMemo(() => {
    const nav = getNav(false, {}, 0, empresaId, 0, 0)
    const avulsos: { to: string; label: string }[] = []
    const grupos: CatGroup[] = []
    for (const item of nav) {
      if (item.superOnly || item.adminOnly) continue  // super/config não são bloqueáveis aqui
      if (item.children?.length) {
        grupos.push({ label: item.label, items: item.children.map(c => ({ to: c.to, label: c.label })) })
      } else {
        avulsos.push({ to: item.to, label: item.label })
      }
    }
    if (avulsos.length) grupos.unshift({ label: 'Telas principais', items: avulsos })
    return grupos
  }, [empresaId])
}

export default function PerfisPage() {
  const { user, empresa } = useAuth()
  const [perfis, setPerfis] = useState<Perfil[]>([])
  const [loading, setLoading] = useState(true)
  const [editando, setEditando] = useState<Perfil | null | 'novo'>(null)

  const canManage = user?.nivel === 'admin' || user?.perfil === 'admin' || user?.perfil === 'super_admin'
  const catalogo = useCatalogo(empresa?.id ?? null)
  const totalTelas = catalogo.reduce((n, g) => n + g.items.length, 0)

  const carregar = async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/auth/perfis')
      setPerfis(data)
    } catch {} finally { setLoading(false) }
  }
  useEffect(() => { carregar() }, [])

  if (!canManage) {
    return (
      <div className="p-8 max-w-xl">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800 text-sm">
          <Shield className="inline mr-2" size={16} />
          Apenas administradores podem gerenciar perfis de acesso.
        </div>
      </div>
    )
  }

  const bloqueadas = (p: Perfil) => {
    if (p.base === 'admin' || !p.permissoes) return 0
    return catalogo.reduce((n, g) => n + g.items.filter(i => p.permissoes![i.to] === false).length, 0)
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Perfis de Acesso</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Defina o que cada perfil enxerga no menu. O <b>nível</b> controla a segurança das ações no servidor.
          </p>
        </div>
        <button onClick={() => setEditando('novo')}
          className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-md hover:bg-brand-700">
          <Plus size={16} /> Novo perfil
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400 text-sm">Carregando...</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {perfis.map(p => {
            const bloq = bloqueadas(p)
            return (
              <button key={p.id} onClick={() => setEditando(p)}
                className="text-left bg-white border border-gray-200 rounded-lg p-4 hover:border-brand-400 hover:shadow-sm transition">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-gray-900">{p.nome}</span>
                  {p.is_sistema
                    ? <span className="inline-flex items-center gap-1 text-[10px] text-gray-500"><Lock size={11} /> Sistema</span>
                    : <span className="text-[10px] text-gray-400">{p.slug}</span>}
                </div>
                <span className={cn('inline-block text-[11px] font-semibold uppercase px-2 py-0.5 rounded', BASE_COLORS[p.base] || 'bg-gray-100 text-gray-700')}>
                  Nível: {p.base}
                </span>
                <p className="text-xs text-gray-500 mt-3">
                  {p.base === 'admin'
                    ? 'Vê todas as telas (acesso total).'
                    : bloq === 0
                      ? `Vê todas as ${totalTelas} telas.`
                      : `${totalTelas - bloq} de ${totalTelas} telas liberadas · ${bloq} bloqueada${bloq > 1 ? 's' : ''}.`}
                </p>
              </button>
            )
          })}
        </div>
      )}

      {editando && (
        <PerfilModal
          perfil={editando === 'novo' ? null : editando}
          catalogo={catalogo}
          onClose={() => setEditando(null)}
          onSaved={() => { setEditando(null); carregar() }}
        />
      )}
    </div>
  )
}


function PerfilModal({ perfil, catalogo, onClose, onSaved }: {
  perfil: Perfil | null
  catalogo: CatGroup[]
  onClose: () => void
  onSaved: () => void
}) {
  const isSistema = !!perfil?.is_sistema
  const [nome, setNome] = useState(perfil?.nome || '')
  const [base, setBase] = useState(perfil?.base || 'operador')
  const [ativo, setAtivo] = useState(perfil?.ativo ?? true)
  // estado de permissões: true = liberado, false = bloqueado. Novo perfil = tudo liberado,
  // EXCETO rotas OPT-IN (escondidas por padrão, ex.: /pdv-online) — só ligam se =true.
  const [perms, setPerms] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {}
    for (const g of catalogo) for (const i of g.items) {
      init[i.to] = OPT_IN.has(i.to)
        ? perfil?.permissoes?.[i.to] === true                 // opt-in: default DESLIGADO
        : (perfil?.permissoes ? perfil.permissoes[i.to] !== false : true)
    }
    return init
  })
  const [loading, setLoading] = useState(false)

  const set = (to: string, v: boolean) => setPerms(s => ({ ...s, [to]: v }))
  const toggleGrupo = (g: CatGroup, v: boolean) => setPerms(s => {
    const next = { ...s }; for (const i of g.items) next[i.to] = v; return next
  })
  const todas = (v: boolean) => setPerms(() => {
    const next: Record<string, boolean> = {}
    for (const g of catalogo) for (const i of g.items) next[i.to] = v
    return next
  })

  const adminBase = base === 'admin'

  const submit = async () => {
    if (!isSistema && nome.trim().length < 2) { toast.error('Informe o nome do perfil'); return }
    setLoading(true)
    try {
      const payload = { nome: nome.trim(), base, ativo, permissoes: perms }
      if (perfil) await api.put(`/auth/perfis/${perfil.id}`, payload)
      else await api.post('/auth/perfis', payload)
      toast.success(perfil ? 'Perfil atualizado' : 'Perfil criado')
      onSaved()
    } catch {} finally { setLoading(false) }
  }

  const excluir = async () => {
    if (!perfil) return
    try {
      await api.delete(`/auth/perfis/${perfil.id}`)
      toast.success('Perfil excluído')
      onSaved()
    } catch {}
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b shrink-0">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Shield size={18} className="text-brand-600" />
            {perfil ? `Editar perfil${isSistema ? ' (sistema)' : ''}` : 'Novo perfil'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1"><XIcon size={18} /></button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome do perfil</label>
              <input type="text" value={nome} disabled={isSistema} onChange={e => setNome(e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm disabled:bg-gray-100 disabled:text-gray-500 focus:ring-2 focus:ring-brand-500"
                placeholder="Ex.: Vendedor, Caixa, Estoquista" />
              {isSistema && <p className="text-[11px] text-gray-400 mt-1">Perfil do sistema — nome e nível são fixos.</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nível de segurança</label>
              <select value={base} disabled={isSistema} onChange={e => setBase(e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm bg-white disabled:bg-gray-100 disabled:text-gray-500 focus:ring-2 focus:ring-brand-500">
                {BASES.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
              <p className="text-[11px] text-gray-400 mt-1">{BASES.find(b => b.value === base)?.hint}</p>
            </div>
          </div>

          {!isSistema && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={ativo} onChange={e => setAtivo(e.target.checked)} /> Perfil ativo
            </label>
          )}

          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-800">Telas visíveis no menu</h3>
              {!adminBase && (
                <div className="flex gap-2">
                  <button onClick={() => todas(true)} className="text-xs text-brand-600 hover:underline">Marcar todas</button>
                  <span className="text-gray-300">·</span>
                  <button onClick={() => todas(false)} className="text-xs text-gray-500 hover:underline">Desmarcar todas</button>
                </div>
              )}
            </div>

            {adminBase ? (
              <div className="bg-brand-50 border border-brand-200 rounded-md p-3 text-sm text-brand-800">
                Nível <b>Administrador</b> enxerga todas as telas — os bloqueios abaixo são ignorados.
              </div>
            ) : (
              <div className="space-y-3">
                {catalogo.map(g => {
                  const marcados = g.items.filter(i => perms[i.to] !== false).length
                  const todosMarcados = marcados === g.items.length
                  return (
                    <div key={g.label} className="border border-gray-200 rounded-md">
                      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
                        <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">{g.label}</span>
                        <button onClick={() => toggleGrupo(g, !todosMarcados)}
                          className={cn('text-[11px] px-2 py-0.5 rounded font-medium',
                            todosMarcados ? 'text-gray-500 hover:bg-gray-200' : 'text-brand-600 hover:bg-brand-50')}>
                          {todosMarcados ? 'Desmarcar grupo' : 'Marcar grupo'}
                        </button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 p-3">
                        {g.items.map(i => (
                          <label key={i.to} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                            <input type="checkbox" checked={perms[i.to] !== false}
                              onChange={e => set(i.to, e.target.checked)} />
                            <span className={cn(perms[i.to] === false && 'text-gray-400 line-through')}>{i.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-4 border-t shrink-0">
          <div>
            {perfil && !isSistema && (
              <button onClick={() => pedirExclusao({ descricao: `o perfil "${perfil.nome}"`, onConfirm: excluir })} className="inline-flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700">
                <Trash2 size={15} /> Excluir
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} disabled={loading}
              className="px-4 py-2 border rounded-md text-sm font-medium hover:bg-gray-50 disabled:opacity-50">Cancelar</button>
            <button onClick={submit} disabled={loading}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand-600 text-white rounded-md text-sm font-medium hover:bg-brand-700 disabled:opacity-50">
              <Check size={15} /> {loading ? 'Salvando...' : (perfil ? 'Salvar' : 'Criar perfil')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
